import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { useEntityOptions } from '../hooks/useEntityOptions';
import { EntitySelect } from '../components/shared/EntitySelect';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Search, Award, Users, Star, Crown, Pencil, Trash2,
  TrendingUp, Clock, User, Mail, Phone, X
} from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(d) { if (!d) return ''; const s = Math.floor((new Date() - new Date(d)) / 1000); if (s < 3600) return `${Math.floor(s / 60)}m ago`; if (s < 86400) return `${Math.floor(s / 3600)}h ago`; return `${Math.floor(s / 86400)}d ago`; }

const TIER_ICONS = { bronze: Award, silver: Star, gold: Crown, platinum: Crown, diamond: Crown };

// ─── Tier Modal ───
function TierModal({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState({ name: '', min_points: 0, multiplier: 1.0, perks: '', color: '#6B7280' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (editing) setForm({ name: editing.name || '', min_points: editing.min_points || 0, multiplier: editing.multiplier || 1.0, perks: (editing.perks || []).join(', '), color: editing.color || '#6B7280' });
    else setForm({ name: '', min_points: 0, multiplier: 1.0, perks: '', color: '#6B7280' });
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, perks: form.perks ? form.perks.split(',').map(p => p.trim()).filter(Boolean) : [] };
      const { data } = editing
        ? await axios.put(`${API}/api/retention/tiers/${editing.id}`, payload)
        : await axios.post(`${API}/api/retention/tiers`, payload);
      toast.success(editing ? 'Tier updated' : 'Tier created');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="flex items-center gap-2"><Award className="h-5 w-5 text-amber-500" />{editing ? 'Edit' : 'New'} Tier</DialogTitle><DialogDescription>Define a loyalty tier level</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Tier Name *</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Gold" data-testid="tier-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Min Points</Label><Input type="number" value={form.min_points} onChange={e => setForm(p => ({...p, min_points: parseInt(e.target.value) || 0}))} data-testid="tier-min-points" /></div>
            <div><Label>Multiplier</Label><Input type="number" step="0.1" value={form.multiplier} onChange={e => setForm(p => ({...p, multiplier: parseFloat(e.target.value) || 1}))} data-testid="tier-multiplier" /></div>
          </div>
          <div><Label>Perks (comma separated)</Label><Input value={form.perks} onChange={e => setForm(p => ({...p, perks: e.target.value}))} placeholder="10% discount, Free shipping" data-testid="tier-perks" /></div>
          <div><Label>Color</Label><div className="flex items-center gap-2"><Input type="color" value={form.color} onChange={e => setForm(p => ({...p, color: e.target.value}))} className="w-12 h-9 p-1" /><span className="text-xs text-gray-400">{form.color}</span></div></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="save-tier-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Create Tier'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

// ─── Member Modal ───
function MemberModal({ open, onClose, onSaved, editing, tiers }) {
  const [form, setForm] = useState({ customer_name: '', customer_email: '', phone: '', points: 0, tier: 'bronze', notes: '' });
  const [saving, setSaving] = useState(false);
  const { contacts } = useEntityOptions();
  useEffect(() => {
    if (editing) setForm({ customer_name: editing.customer_name || '', customer_email: editing.customer_email || '', phone: editing.phone || '', points: editing.points || 0, tier: editing.tier || 'bronze', notes: editing.notes || '' });
    else setForm({ customer_name: '', customer_email: '', phone: '', points: 0, tier: 'bronze', notes: '' });
  }, [editing, open]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const { data } = editing
        ? await axios.put(`${API}/api/retention/members/${editing.id}`, form)
        : await axios.post(`${API}/api/retention/members`, form);
      toast.success(editing ? 'Member updated' : 'Member added');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  const tierOptions = tiers.length > 0 ? tiers : [{ id: 'bronze', name: 'Bronze' }, { id: 'silver', name: 'Silver' }, { id: 'gold', name: 'Gold' }];
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-pink-500" />{editing ? 'Edit' : 'Add'} Member</DialogTitle><DialogDescription>Manage loyalty member</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div><Label>Customer Name *</Label><EntitySelect value={form.customer_name} onChange={v => setForm(p => ({...p, customer_name: v}))} onSelect={opt => setForm(p => ({...p, customer_email: opt.email || p.customer_email }))} options={contacts} placeholder="Select or type customer name" listId="loyalty-clients" inputProps={{ "data-testid": "member-name" }} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input value={form.customer_email} onChange={e => setForm(p => ({...p, customer_email: e.target.value}))} data-testid="member-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm(p => ({...p, phone: e.target.value}))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Points</Label><Input type="number" value={form.points} onChange={e => setForm(p => ({...p, points: parseInt(e.target.value) || 0}))} data-testid="member-points" /></div>
            <div><Label>Tier</Label>
              <Select value={form.tier} onValueChange={v => setForm(p => ({...p, tier: v}))}>
                <SelectTrigger data-testid="member-tier"><SelectValue /></SelectTrigger>
                <SelectContent>{tierOptions.map(t => <SelectItem key={t.name?.toLowerCase() || t.id} value={t.name?.toLowerCase() || t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="save-member-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Add Member'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

export default function LoyaltyPage() {
  const [tiers, setTiers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [tab, setTab] = useState('members');
  const [tierModal, setTierModal] = useState(false);
  const [memberModal, setMemberModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [stats, setStats] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [tiersRes, membersRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/retention/tiers`),
        axios.get(`${API}/api/retention/members`, { params: { ...(filterTier !== 'all' && { tier: filterTier }), ...(search && { search }) },  }),
        axios.get(`${API}/api/retention/stats`),
      ]);
      setTiers(tiersRes.data);
      setMembers(membersRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [search, filterTier]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTierSaved = (t, isUpdate) => { if (isUpdate) setTiers(p => p.map(x => x.id === t.id ? t : x)); else setTiers(p => [...p, t]); };
  const handleMemberSaved = (m, isUpdate) => { if (isUpdate) setMembers(p => p.map(x => x.id === m.id ? m : x)); else setMembers(p => [m, ...p]); };
  const deleteTier = async (id) => { if (!window.confirm('Delete tier?')) return; try { await axios.delete(`${API}/api/retention/tiers/${id}`); setTiers(p => p.filter(t => t.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };
  const deleteMember = async (id) => { if (!window.confirm('Remove member?')) return; try { await axios.delete(`${API}/api/retention/members/${id}`); setMembers(p => p.filter(m => m.id !== id)); toast.success('Removed'); } catch { toast.error('Failed'); } };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="loyalty-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><Award className="h-5 w-5 text-amber-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Loyalty Program<PageInfoTooltip pageKey="loyalty-program" /></h1><p className="text-xs text-gray-400">{members.length} members · {tiers.length} tiers</p></div></div>
        <div className="flex items-center gap-2">
          <Button variant={tab === 'members' ? 'default' : 'outline'} size="sm" onClick={() => setTab('members')} className={tab === 'members' ? 'bg-pink-600 hover:bg-pink-700' : ''} data-testid="tab-members">Members</Button>
          <Button variant={tab === 'tiers' ? 'default' : 'outline'} size="sm" onClick={() => setTab('tiers')} className={tab === 'tiers' ? 'bg-amber-600 hover:bg-amber-700' : ''} data-testid="tab-tiers">Tiers</Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Members', val: stats.total_members, color: 'bg-pink-50 text-pink-700' },
            { label: 'Active Referrals', val: stats.active_referrals, color: 'bg-blue-50 text-blue-700' },
            { label: 'Conversions', val: stats.total_conversions, color: 'bg-green-50 text-green-700' },
            { label: 'Win-Back Campaigns', val: stats.active_campaigns, color: 'bg-purple-50 text-purple-700' },
          ].map((s, i) => (
            <Card key={i} className="border-gray-200"><CardContent className="p-3 text-center"><p className={`text-xl font-bold ${s.color.split(' ')[1]}`}>{s.val}</p><p className="text-[10px] text-gray-400">{s.label}</p></CardContent></Card>
          ))}
        </div>
      )}

      {/* Tiers Tab */}
      {tab === 'tiers' && (
        <div className="space-y-3">
          <div className="flex justify-end"><Button onClick={() => { setEditingTier(null); setTierModal(true); }} className="bg-amber-600 hover:bg-amber-700 gap-1.5 h-9" data-testid="new-tier-btn"><Plus className="h-4 w-4" /> Add Tier</Button></div>
          {loading ? <div className="flex justify-center py-10"><div className="w-8 h-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" /></div> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {tiers.map(t => (
                <Card key={t.id} className="border-gray-200 overflow-hidden" data-testid={`tier-card-${t.id}`}>
                  <div className="h-2" style={{ backgroundColor: t.color }} />
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-bold" style={{ color: t.color }}>{t.name}</h3>
                      <Badge variant="outline" className="text-[9px]">{t.multiplier}x</Badge>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{t.min_points}+ points required</p>
                    <div className="space-y-1 mb-3">{(t.perks || []).map((p, i) => <p key={i} className="text-[11px] text-gray-600 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-gray-400" />{p}</p>)}</div>
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingTier(t); setTierModal(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteTier(t.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Members Tab */}
      {tab === 'members' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-xs"><Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search members..." className="pl-9 h-9 text-sm" data-testid="member-search" /></div>
            <Select value={filterTier} onValueChange={setFilterTier}><SelectTrigger className="w-28 h-9 text-sm"><SelectValue placeholder="Tier" /></SelectTrigger><SelectContent><SelectItem value="all">All Tiers</SelectItem>{tiers.map(t => <SelectItem key={t.id} value={t.name.toLowerCase()}>{t.name}</SelectItem>)}</SelectContent></Select>
            <Button onClick={() => { setEditingMember(null); setMemberModal(true); }} className="bg-pink-600 hover:bg-pink-700 gap-1.5 h-9" data-testid="new-member-btn"><Plus className="h-4 w-4" /> Add Member</Button>
          </div>
          {loading ? <div className="flex justify-center py-10"><div className="w-8 h-8 rounded-full border-2 border-pink-500 border-t-transparent animate-spin" /></div> : members.length === 0 ? (
            <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center"><Users className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-700 mb-1">No members yet</h3><Button onClick={() => { setEditingMember(null); setMemberModal(true); }} className="bg-pink-600 hover:bg-pink-700 gap-1.5 mt-3"><Plus className="h-4 w-4" /> Add Member</Button></CardContent></Card>
          ) : (
            <div className="space-y-2">
              {members.map(m => {
                const tierData = tiers.find(t => t.name.toLowerCase() === m.tier);
                return (
                  <Card key={m.id} className="border-gray-200 hover:shadow-sm transition-shadow" data-testid={`member-row-${m.id}`}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: tierData?.color || '#6B7280' }}>
                        {m.customer_name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2"><h3 className="text-sm font-semibold text-gray-900">{m.customer_name}</h3><Badge className="text-[9px]" style={{ backgroundColor: `${tierData?.color || '#6B7280'}20`, color: tierData?.color || '#6B7280' }}>{m.tier}</Badge></div>
                        <div className="flex items-center gap-3 mt-0.5 text-[10px] text-gray-400">
                          {m.customer_email && <span className="flex items-center gap-0.5"><Mail className="h-2.5 w-2.5" />{m.customer_email}</span>}
                          {m.phone && <span className="flex items-center gap-0.5"><Phone className="h-2.5 w-2.5" />{m.phone}</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-lg font-bold text-gray-800">{m.points.toLocaleString()}</p>
                        <p className="text-[10px] text-gray-400">points</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingMember(m); setMemberModal(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteMember(m.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      <TierModal open={tierModal} onClose={() => { setTierModal(false); setEditingTier(null); }} onSaved={handleTierSaved} editing={editingTier} />
      <MemberModal open={memberModal} onClose={() => { setMemberModal(false); setEditingMember(null); }} onSaved={handleMemberSaved} editing={editingMember} tiers={tiers} />
    </div>
  );
}
